import { useEffect, useRef, useState } from "react";

import { isBefore } from "date-fns";
import { InfinityIcon } from "lucide-react";

import { SCENARIO_ASSIGNMENT_STATUS_STYLES } from "@/components/pages_logged_in/roleplays/utils";
import { Button } from "@/components/ui/button";
import {
  ScenarioAssignmentDisplayStatus,
  UnifiedAssignmentType,
} from "@/graphql";
import { formatDate } from "@/utils/datetime";

export interface ScenarioAssignmentBannerProps {
  unifiedAssignments: (UnifiedAssignmentType | null)[];
  // Optional props for multiple assignments
  isMultipleAssignments?: boolean;
  firstDueDate?: string;
  pastDueCount?: number;
  // Optional callback for submit action
  onSubmit?: () => void;
  isReadyToSubmit?: boolean;
  onNumberOfAssignmentsClick?: () => void;
  showAssignerName?: boolean;
  visibleWhenAllCompleted?: boolean;
}

interface SessionAttemptsProps {
  completed: number;
  minimum: number | null;
  maximum: number | null;
  status?: ScenarioAssignmentDisplayStatus;
  showDot?: boolean;
}

export function SessionAttempts({
  completed,
  minimum,
  maximum,
  status,
}: SessionAttemptsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const checkWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setIsVisible(width >= 280);
      }
    };

    checkWidth();

    const resizeObserver = new ResizeObserver(checkWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const isPastDue = status === ScenarioAssignmentDisplayStatus.PastDue;
  const minimumRequired = minimum || 1;
  const maximumAllowed = maximum || minimumRequired;
  const remainingToMinimum = Math.max(0, minimumRequired - completed);
  const isMinimumMet = completed >= minimumRequired;

  // Determine dot colors and states
  const getDotColor = (index: number) => {
    if (completed > index) {
      return "rgba(39, 174, 96, 0.7)"; // Completed - green
    }
    if (isPastDue) {
      return "rgba(0, 0, 0, 0.1)"; // Past due - faded
    }
    return "rgba(0, 0, 0, 0.1)"; // Not completed - faded
  };

  // Determine text content and color
  const getProgressText = () => {
    if (remainingToMinimum > 0) {
      const sessionWord = remainingToMinimum === 1 ? "session" : "sessions";
      return `${remainingToMinimum} more ${sessionWord}`;
    }

    if (maximumAllowed && completed < maximumAllowed) {
      const remaining = maximumAllowed - completed;
      const sessionWord = remaining === 1 ? "session" : "sessions";
      return `${remaining} more ${sessionWord}`;
    }

    return "";
  };

  const getTextColor = () => {
    if (isMinimumMet) {
      return "#2E3C47"; // Dark text for ready to submit
    }
    if (isPastDue) {
      return SCENARIO_ASSIGNMENT_STATUS_STYLES[
        ScenarioAssignmentDisplayStatus.PastDue
      ].textColor; // Orange for past due
    }
    return "#778397"; // Gray for default
  };

  const dotComponent = (
    <div className="flex items-center gap-1 shrink-0">
      {maximum ? (
        // Show all dots up to maximum with divider at minimum
        <div className="flex items-center gap-1 shrink-0">
          {/* Dots from 1 to minimum */}
          {Array.from({ length: minimumRequired }).map((_, index) => (
            <div
              key={`min-${index}`}
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getDotColor(index) }}
            />
          ))}
          {/* Divider if we have more than minimum */}
          {maximumAllowed > minimumRequired && (
            <div
              className="w-[1px] h-3 rounded-sm"
              style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
            />
          )}
          {/* Dots from minimum+1 to maximum */}
          {Array.from({ length: maximumAllowed - minimumRequired }).map(
            (_, index) => (
              <div
                key={`max-${index}`}
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: getDotColor(minimumRequired + index),
                }}
              />
            ),
          )}
        </div>
      ) : (
        // Original behavior for unlimited sessions
        <div className="flex items-center gap-1 shrink-0">
          {Array.from({ length: minimumRequired }).map((_, index) => (
            <div
              key={index}
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: getDotColor(index) }}
            />
          ))}
          {isMinimumMet && (
            <>
              <div
                className="w-[1px] h-3 rounded-sm"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
              />
              <InfinityIcon
                className="w-6 h-6"
                strokeWidth={1.5}
                style={{ color: "rgba(0, 0, 0, 0.2)" }}
              />
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex items-center gap-2 flex-1" ref={containerRef}>
      {isVisible && dotComponent}
      <span className="text-sm shrink-0" style={{ color: getTextColor() }}>
        {getProgressText()}
      </span>
    </div>
  );
}

export function ScenarioAssignmentBanner({
  unifiedAssignments,
  onNumberOfAssignmentsClick,
  showAssignerName = true,
  visibleWhenAllCompleted = false,
}: ScenarioAssignmentBannerProps) {
  // Filter out null values first
  const validAssignments = (
    unifiedAssignments?.filter(
      (assignment): assignment is UnifiedAssignmentType => assignment !== null,
    ) ?? []
  ).sort((a, b) => {
    // Define status priority order: PastDue, InProgress, Completed, DidNotPass
    const statusPriority: Record<ScenarioAssignmentDisplayStatus, number> = {
      [ScenarioAssignmentDisplayStatus.PastDue]: 0,
      [ScenarioAssignmentDisplayStatus.NotStarted]: 1,
      [ScenarioAssignmentDisplayStatus.InProgress]: 2,
      [ScenarioAssignmentDisplayStatus.Completed]: 3,
      [ScenarioAssignmentDisplayStatus.DidNotPass]: 4,
    };

    // Get priority values for each status (default to 5 if status is undefined)
    const aPriority = a.displayStatus
      ? (statusPriority[a.displayStatus] ?? 5)
      : 5;
    const bPriority = b.displayStatus
      ? (statusPriority[b.displayStatus] ?? 5)
      : 5;

    // First sort by status priority
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // If same status, sort by dueDate ascending (earliest first), null/undefined dueDates go last
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });
  if (validAssignments.length === 0) {
    return null;
  }

  const completedAssignments = validAssignments.filter(
    (assignment) =>
      assignment.displayStatus === ScenarioAssignmentDisplayStatus.Completed ||
      assignment.displayStatus === ScenarioAssignmentDisplayStatus.DidNotPass,
  );

  if (
    !visibleWhenAllCompleted &&
    completedAssignments.length === validAssignments.length
  ) {
    return null;
  }

  const firstAssignment = validAssignments[0];
  const numberOfAssignments = validAssignments.length;
  const isMultipleAssignments = numberOfAssignments > 1;

  // Count past due assignments once, reuse
  const pastDueCount = validAssignments.reduce(
    (count, assignment) =>
      assignment.displayStatus === ScenarioAssignmentDisplayStatus.PastDue
        ? count + 1
        : count,
    0,
  );

  // Pick status style: PastDue if any past due and multiple, else first assignment's status
  const assignmentStatusStyle =
    isMultipleAssignments && pastDueCount > 0
      ? SCENARIO_ASSIGNMENT_STATUS_STYLES[
          ScenarioAssignmentDisplayStatus.PastDue
        ]
      : firstAssignment?.displayStatus
        ? SCENARIO_ASSIGNMENT_STATUS_STYLES[firstAssignment.displayStatus]
        : undefined;

  if (!assignmentStatusStyle) {
    return null;
  }

  // Find earliest due date for multiple assignments, else just the first
  const firstDueDate = isMultipleAssignments
    ? validAssignments.reduce((min, assignment) => {
        if (!assignment?.dueDate || !min) return assignment?.dueDate || min;
        return isBefore(new Date(min), new Date(assignment.dueDate))
          ? min
          : assignment.dueDate;
      }, firstAssignment?.dueDate)
    : firstAssignment?.dueDate;

  // Extract properties safely from Union type
  const completedAssignmentCount = firstAssignment?.completedCount ?? 0;

  const minNumberOfAssignment = firstAssignment.minAttempt ?? 0;
  const maxNumberOfAssignment = firstAssignment.maxAttempt ?? 0;

  // For multiple assignments, show a different layout
  if (isMultipleAssignments) {
    const havingPastDueAssignments = pastDueCount > 0;
    const bgColor = assignmentStatusStyle.backgroundColor;

    return (
      <div
        className="px-3 py-1.5 rounded-sm w-full "
        style={{ backgroundColor: bgColor }}
      >
        <div className="flex-1 flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="text-sm font-medium bg-transparent hidden lg:block"
              style={{
                borderColor: "rgba(206, 213, 222, 0.5)",
                color: "#2E3C47",
              }}
              onClick={onNumberOfAssignmentsClick}
            >
              {numberOfAssignments} Assignment
              {numberOfAssignments !== 1 ? "s" : ""}
            </Button>
            <span className="text-gray-600 hidden lg:block">·</span>
            <span
              className="text-sm"
              style={{ color: assignmentStatusStyle.textColor }}
            >
              First Due {formatDate(firstDueDate, "MMM dd, yyyy")}
            </span>
          </div>

          {havingPastDueAssignments && (
            <div
              className="flex items-center px-2 py-1 rounded-sm gap-2 text-sm"
              style={{
                backgroundColor:
                  SCENARIO_ASSIGNMENT_STATUS_STYLES[
                    ScenarioAssignmentDisplayStatus.PastDue
                  ].backgroundColor,
              }}
            >
              {
                SCENARIO_ASSIGNMENT_STATUS_STYLES[
                  ScenarioAssignmentDisplayStatus.PastDue
                ].icon
              }
              <span
                style={{
                  color:
                    SCENARIO_ASSIGNMENT_STATUS_STYLES[
                      ScenarioAssignmentDisplayStatus.PastDue
                    ].textColor,
                }}
              >
                {pastDueCount} Past Due
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Standard single assignment layout
  return (
    <div
      className="px-3 py-1.5 rounded-sm flex items-center w-full"
      style={{ backgroundColor: assignmentStatusStyle.backgroundColor }}
    >
      <div className="flex items-center gap-3 w-full">
        <div className="flex items-center gap-3 flex-1">
          {showAssignerName && (
            <>
              <span className="text-sm font-semibold text-gray-800 shrink-0 hidden lg:block">
                Assigned by {firstAssignment?.assignerName}
              </span>
              <span className="text-gray-600 hidden lg:block">·</span>
            </>
          )}
          <span className="text-sm text-gray-600 shrink-0">
            Due {formatDate(firstAssignment?.dueDate, "MMM dd, yyyy")}
          </span>
          <span className="text-gray-600">·</span>
          <SessionAttempts
            completed={completedAssignmentCount || 0}
            minimum={minNumberOfAssignment || 0}
            maximum={maxNumberOfAssignment || 0}
            status={firstAssignment?.displayStatus!}
          />
        </div>
        <div
          className="flex items-center px-2 py-1 rounded-sm gap-2 shrink-0 text-sm"
          style={{ backgroundColor: assignmentStatusStyle.backgroundColor }}
        >
          {assignmentStatusStyle.icon}
          <span style={{ color: assignmentStatusStyle.textColor }}>
            {assignmentStatusStyle.text}
          </span>
        </div>
      </div>
    </div>
  );
}
